/**
 * VerCredenciamentoPage — visualização do Edital de Chamamento gerado
 * (fase Edital).
 *
 * Réplica do view_credenciamento.html: metadados (hipótese do art. 79,
 * distribuição, valor), validação de conformidade e o documento em texto
 * corrido — não há edição por seções.
 */

import {
  faArrowLeft,
  faCheckCircle,
  faClipboardCheck,
  faExclamationTriangle,
  faFileAlt,
  faFileWord,
  faIdCard,
  faRedo,
  faSpinner,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import {
  documentoCredenciamento,
  excluirCredenciamento,
  type DocumentoCredenciamento,
} from '../../api/credenciamento'
import { useToast } from '../../components/Toast'

function CartaoMetadado({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-dark-card">
      <div className="mb-1 text-xs text-slate-500">{rotulo}</div>
      <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
        {children}
      </div>
    </div>
  )
}

function ModalExcluir({
  chamamento, aoFechar,
}: {
  chamamento: DocumentoCredenciamento
  aoFechar: () => void
}) {
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()

  const excluir = useMutation({
    mutationFn: () => excluirCredenciamento(chamamento.id),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['processos-licitatorios'] })
      notificar('success', 'Edital de Chamamento excluído. O processo e o restante da cadeia foram mantidos.')
      navegar('/processos')
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog" aria-modal="true" aria-label="Excluir Edital de Chamamento"
      onMouseDown={(e) => { if (e.target === e.currentTarget) aoFechar() }}
    >
      <div className="mx-4 w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-2xl dark:border-slate-700 dark:bg-dark-card">
        <span className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-orange-600 shadow-lg shadow-red-500/30">
          <FontAwesomeIcon icon={faTrashAlt} className="text-2xl text-white" />
        </span>
        <h3 className="mb-2 text-xl font-bold text-slate-800 dark:text-slate-100">
          Excluir Edital de Chamamento?
        </h3>
        <p className="mb-4 rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-800 dark:bg-slate-700 dark:text-slate-200">
          {chamamento.objeto}
        </p>
        <p className="mb-6 text-sm font-semibold text-red-600 dark:text-red-400">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
          Esta ação não pode ser desfeita!
        </p>
        <div className="flex justify-center gap-3">
          <button type="button" onClick={aoFechar} className="rounded-xl bg-slate-200 px-6 py-3 font-medium text-slate-700 transition-all hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300">
            Cancelar
          </button>
          <button type="button" onClick={() => excluir.mutate()} disabled={excluir.isPending} className="rounded-xl bg-gradient-to-r from-red-500 to-orange-600 px-6 py-3 font-semibold text-white shadow-lg shadow-red-500/30 transition-all hover:from-red-600 hover:to-orange-700 disabled:opacity-60">
            <FontAwesomeIcon icon={excluir.isPending ? faSpinner : faTrashAlt} spin={excluir.isPending} className="mr-2" />
            Sim, Excluir
          </button>
        </div>
      </div>
    </div>
  )
}

export function VerCredenciamentoPage() {
  const { id } = useParams()
  const idNumerico = Number(id)
  const [excluindo, setExcluindo] = useState(false)

  const { data: chamamento, isLoading, error } = useQuery({
    queryKey: ['credenciamento-documento', idNumerico],
    queryFn: () => documentoCredenciamento(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando Edital de Chamamento…
      </p>
    )
  }
  if (error || !chamamento) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <FontAwesomeIcon icon={faIdCard} className="mb-4 text-5xl text-slate-300" />
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">
          Edital de Chamamento não encontrado
        </h1>
        <p className="mb-6 text-slate-500">
          {(error as Error)?.message ?? 'O documento não existe ou não é seu.'}
        </p>
        <Link to="/processos" className="inline-flex items-center rounded-lg bg-teal-600 px-6 py-3 font-semibold text-white hover:bg-teal-700">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>
    )
  }

  const { validacao } = chamamento

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-6 rounded-2xl bg-gradient-to-r from-teal-500 via-cyan-600 to-teal-500 p-6 text-white shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-3 text-2xl font-bold">
              <FontAwesomeIcon icon={faIdCard} />
              Edital de Chamamento
              {chamamento.numero_edital && ` Nº ${chamamento.numero_edital}`}
            </h1>
            <p className="mt-2 text-sm text-teal-100">{chamamento.objeto}</p>
          </div>
          <div className="flex gap-2">
            <a href={`/credenciamento/${chamamento.id}/export/docx`}
              className="rounded-lg bg-white/20 px-4 py-2 text-sm font-medium transition-all hover:bg-white/30">
              <FontAwesomeIcon icon={faFileWord} className="mr-2" />
              Exportar DOCX
            </a>
            <Link to={`/credenciamento/novo/${chamamento.id}`}
              className="rounded-lg bg-white/20 px-4 py-2 text-sm font-medium transition-all hover:bg-white/30">
              <FontAwesomeIcon icon={faRedo} className="mr-2" />
              Gerar Novamente
            </Link>
          </div>
        </div>
      </header>

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <CartaoMetadado rotulo="Hipótese Legal">
          Art. 79, {chamamento.hipotese_descricao || '-'}
        </CartaoMetadado>
        <CartaoMetadado rotulo="Distribuição">
          {chamamento.distribuicao_descricao || '-'}
        </CartaoMetadado>
        <CartaoMetadado rotulo="Valor Estimado">
          {chamamento.valor_estimado || '-'}
        </CartaoMetadado>
        <CartaoMetadado rotulo="Conformidade Art. 79">
          <span className={validacao.valido ? 'text-green-600' : 'text-amber-600'}>
            <FontAwesomeIcon
              icon={validacao.valido ? faCheckCircle : faExclamationTriangle}
              className="mr-1"
            />
            {validacao.valido ? 'Validado' : 'Com ressalvas'}
          </span>
        </CartaoMetadado>
      </div>

      {(validacao.avisos.length > 0 || validacao.erros.length > 0) && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
          <h3 className="mb-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
            <FontAwesomeIcon icon={faClipboardCheck} className="mr-2" />
            Observações da validação automática
          </h3>
          <ul className="list-disc space-y-1 pl-5 text-xs text-amber-700 dark:text-amber-400">
            {validacao.erros.map((erro) => (
              <li key={erro} className="font-semibold">{erro}</li>
            ))}
            {validacao.avisos.map((aviso) => (
              <li key={aviso}>{aviso}</li>
            ))}
          </ul>
        </div>
      )}

      <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-700/40">
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            <FontAwesomeIcon icon={faFileAlt} className="mr-2 text-teal-600" />
            Documento Completo
          </span>
          <span className="text-xs text-slate-500">
            {chamamento.total_secoes || '-'} seções ·{' '}
            {chamamento.total_caracteres.toLocaleString('pt-BR')} caracteres
            {chamamento.gerado_em && ` · Gerado em ${chamamento.gerado_em}`}
          </span>
        </div>
        <div className="p-6 md:p-10">
          <div
            className="conteudo-documento font-serif text-[15px] leading-relaxed text-justify text-slate-700 dark:text-slate-300"
            dangerouslySetInnerHTML={{ __html: chamamento.html }}
          />
        </div>
      </article>

      <div className="mt-6 flex items-center justify-between">
        <Link to="/processos" className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 dark:text-slate-300">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
          Voltar aos Processos
        </Link>
        <button
          type="button"
          onClick={() => setExcluindo(true)}
          className="rounded-lg px-4 py-2 text-sm text-red-600 transition-all hover:bg-red-50 hover:text-red-800 dark:hover:bg-red-900/20"
        >
          <FontAwesomeIcon icon={faTrashAlt} className="mr-1" />
          Excluir Edital de Chamamento
        </button>
      </div>

      {excluindo && (
        <ModalExcluir chamamento={chamamento} aoFechar={() => setExcluindo(false)} />
      )}
    </div>
  )
}
